import React from 'react';
import { Info, Shield, Database, Server, Lock, FileCheck, RefreshCw, Users, CheckCircle } from 'lucide-react';

const SYSTEM_VERSION = '1.0.0';
const BUILD_DATE = '20/06/2026';

const Section = ({ title, icon: Icon, children }) => (
  <div className="card p-5">
    <div className="flex items-center gap-2 mb-4 border-b border-gray-700 pb-3">
      <Icon size={18} className="text-blue-400" />
      <h2 className="text-base font-semibold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between py-2 border-b border-gray-700/40 last:border-0">
    <span className="text-sm text-gray-400">{label}</span>
    <span className="text-sm text-white text-right max-w-xs">{value}</span>
  </div>
);

const Badge = ({ ok, label }) => (
  <div className="flex items-center gap-2 py-1.5">
    <CheckCircle size={15} className={ok ? 'text-green-400' : 'text-gray-500'} />
    <span className={`text-sm ${ok ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
  </div>
);

const AboutSystem = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center gap-3">
      <Info size={28} className="text-blue-400" />
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Sobre o Sistema</h1>
        <p className="text-gray-400 text-sm">F4-01 / F4-02 — Documentação técnica e informações do sistema — GAMP 5, RDC 658/2022</p>
      </div>
    </div>

    {/* Version Banner */}
    <div className="card p-6 bg-gradient-to-r from-blue-900/40 to-blue-800/20 border-blue-700/40 flex items-center justify-between">
      <div>
        <p className="text-xs text-blue-300 uppercase tracking-wider mb-1">Versão do Sistema</p>
        <p className="text-4xl font-bold text-white">v{SYSTEM_VERSION}</p>
        <p className="text-sm text-gray-400 mt-1">Build: {BUILD_DATE}</p>
      </div>
      <div className="text-right">
        <p className="text-sm text-gray-400">QualySynX SGQ</p>
        <p className="text-xs text-gray-500">Sistema de Gestão de Qualidade</p>
        <p className="text-xs text-gray-500 mt-1">Ambiente: Produção</p>
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Technical Info */}
      <Section title="Informações Técnicas" icon={Server}>
        <Row label="Versão" value={`v${SYSTEM_VERSION}`} />
        <Row label="Data de Build" value={BUILD_DATE} />
        <Row label="Framework Backend" value="FastAPI (Python 3.12)" />
        <Row label="Framework Frontend" value="React 18 + Tailwind CSS" />
        <Row label="Banco de Dados" value="PostgreSQL 15" />
        <Row label="Autenticação" value="JWT (Bearer Token)" />
        <Row label="Protocolo" value="HTTPS (TLS 1.3)" />
        <Row label="Fuso Horário" value="America/Sao_Paulo (UTC-3)" />
      </Section>

      {/* Compliance */}
      <Section title="Conformidade Normativa" icon={Shield}>
        <Row label="ISO 9001:2015" value="Gestão de Qualidade" />
        <Row label="ISO 13485:2016" value="Dispositivos Médicos" />
        <Row label="RDC 658/2022 — ANVISA" value="Validação de Sistemas Computadorizados" />
        <Row label="RDC 430/2020 — ANVISA" value="Boas Práticas de Fabricação" />
        <Row label="GAMP 5 (ISPE)" value="Guia para Sistemas Computadorizados" />
        <Row label="PIC/S PI 011" value="Integridade de Dados" />
      </Section>

      {/* F4 Implemented Features */}
      <Section title="Controles de Conformidade Implementados (F4)" icon={FileCheck}>
        <Badge ok label="F4-01 — Versão do sistema visível em rodapé e tela de login" />
        <Badge ok label="F4-02 — Documentação técnica (esta tela)" />
        <Badge ok label="F4-03 — Trilha de auditoria com registro de todas as ações" />
        <Badge ok label="F4-04 — Política de backup documentada" />
        <Badge ok label="F4-05 — Bloqueio após 5 tentativas de login inválidas (15 min)" />
        <Badge ok label="F4-06 — Assinatura eletrônica por confirmação de senha" />
        <Badge ok label="F4-07 — Soft delete com justificativa de exclusão" />
        <Badge ok label="F4-08 — Dados ALCOA: atribuíveis, contemporâneos, originais" />
        <Badge ok label="F4-09 — Revisão periódica anual do sistema" />
        <Badge ok label="F4-10 — Perfis com permissões granulares por módulo" />
      </Section>

      {/* Access Control */}
      <Section title="Controle de Acesso (F4-05)" icon={Lock}>
        <Badge ok label="Autenticação individual com senha por usuário" />
        <Badge ok label="Bloqueio automático após 5 tentativas inválidas" />
        <Badge ok label="Desbloqueio automático após 15 minutos" />
        <Badge ok label="Sessão expirada por token JWT com prazo definido" />
        <Badge ok label="Inativação de usuário desligado pelo administrador" />
        <Badge ok label="Perfis de acesso por módulo (Visualizar / Criar / Editar / Excluir / Aprovar)" />
      </Section>

      {/* Data Integrity */}
      <Section title="Integridade de Dados — ALCOA (F4-08)" icon={Database}>
        <Row label="Atribuível" value="Todos os registros possuem usuário criador/editor" />
        <Row label="Legível" value="Dados estruturados e com labels claros no sistema" />
        <Row label="Contemporâneo" value="Timestamps automáticos (created_at / updated_at)" />
        <Row label="Original" value="Dados salvos diretamente sem transformação intermediária" />
        <Row label="Preciso" value="Validações de formato e campos obrigatórios em todos os formulários" />
        <Row label="Rastreável" value="Trilha de auditoria registra CREATE / UPDATE / DELETE / APPROVE" />
      </Section>

      {/* Backup Policy */}
      <Section title="Política de Backup (F4-04)" icon={RefreshCw}>
        <Badge ok label="Banco de dados: PostgreSQL gerenciado com WAL archiving" />
        <Badge ok label="Periodicidade recomendada: backup diário + incremental contínuo" />
        <Badge ok label="Retenção mínima: 5 anos (RDC 658/2022)" />
        <Badge ok label="Teste de restauração: trimestral obrigatório" />
        <Badge ok label="Localização: servidor remoto redundante (offsite)" />
        <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-400">
          ⚠ O Administrador do Sistema é responsável por configurar e documentar os testes de restauração conforme procedimento POP-TI-001.
        </div>
      </Section>

      {/* Users/Profiles */}
      <Section title="Responsabilidades de Perfis (F4-10)" icon={Users}>
        <Row label="Administrador" value="Acesso total — todos os módulos + configurações" />
        <Row label="Analista" value="Criar, editar e visualizar registros; sem exclusão" />
        <Row label="Aprovador" value="Visualizar e aprovar registros; sem criação/exclusão" />
        <Row label="Gestor de Setor" value="Acesso ao módulo de seu setor; criação e edição" />
        <Row label="Visualizador" value="Apenas leitura — sem alterações no sistema" />
        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-400">
          ℹ Permissões granulares configuráveis em <strong>Usuários e Perfis → Perfis</strong>.
        </div>
      </Section>
    </div>
  </div>
);

export default AboutSystem;
